import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Tab, Tabs } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { CreateUpdateCompanyDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateCompanyDto';
import { validate } from '../../../common/spdCore/contexts/CompanyContext';
import { ReactSelectOption } from '../Selects/SortableSelect';
import {
  GetBatteryDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import LastEditorInfo from '../../../app/components/LastEditorInfo';

import { CompanyEditorTabData } from './CompanyEditorTabData';
import { CompanyEditorTabExternalReferral } from './CompanyEditorTabExternalReferral';
import { CompanyEditorTabInvoicing } from './CompanyEditorTabInvoicing';

interface CompanyEditorProps {
  id?: string;
  company: CreateUpdateCompanyDto;
  batteries?: GetBatteryDto[];
  handleClose?: () => void;
  show?: boolean;
  readOnly: boolean;
}

export const CompanyEditor: React.FC<CompanyEditorProps> = props => {
  const spd = useSpdCore();
  const [company, setCompany] = useState(props.company);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [batteries, setBatteries] = useState<ReactSelectOption[]>([]);

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      const bats =
        props.batteries?.map(b => ({ value: b.id, label: b.name || '' })) || [];
      setBatteries(bats);
      setCompany({ ...props.company, batteries: bats.map(o => o.value) });
    }
  }, [props.show, props.company, props.batteries]);

  const [isGusLoading, setIsGusLoading] = useState(false);

  const findByNip = async () => {
    const nip = company?.nip?.trim();
    if (!nip) {
      setSaveErrors(['Wprowadź NIP przed wyszukaniem w GUS.']);
      setShowSaveErrors(true);
      return;
    }
    setIsGusLoading(true);
    try {
      const nipClient = await spd.companies.getByNip(nip);
      if (nipClient) {
        setShowSaveErrors(false);
        propertyChange({
          name: company.name || nipClient.name,
          officialName: nipClient.name || company.name,
          address: [
            nipClient.street,
            nipClient.numbers,
            nipClient.flat ? `/${nipClient.flat}` : '',
            nipClient.postcode,
            nipClient.place,
          ]
            .filter(Boolean)
            .join(' ')
            .trim(),
        });
      }
    } catch (response) {
      setSaveErrors(await responseErrors(response as ResponseError));
      setShowSaveErrors(true);
    } finally {
      setIsGusLoading(false);
    }
  };

  const createCompany = async () => {
    const status = await validate(company);
    if (!status.valid) {
      setSaveErrors(status.errors);
      setShowSaveErrors(true);
      return;
    }
    try {
      if (props.id) {
        await spd.companies.update(props.id, company);
      } else {
        await spd.companies.create(company);
      }
      if (props.handleClose) {
        props.handleClose();
      }
    } catch (response) {
      setSaveErrors(await responseErrors(response as ResponseError));
      setShowSaveErrors(true);
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateCompanyDto>) => {
    setCompany({ ...company, ...obj });
  };

  const setTitle = () => {
    return props.id && !props.readOnly
      ? 'Edycja firmy'
      : props.id
        ? 'Podgląd firmy'
        : 'Dodawanie nowej firmy';
  };

  const handleClick = async (tempBatteries: ReactSelectOption[]) => {
    setCompany({ ...company, batteries: tempBatteries.map(o => o.value) });
    setBatteries(tempBatteries);
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="xl"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={(event: any) => event.stopPropagation()}
      >
        <Modal.Header closeButton>
          <Modal.Title>{setTitle()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey="data" id="companyEditorTabs" className="mb-2">
            <Tab eventKey="data" title="Dane podstawowe" className="mb-2">
              <CompanyEditorTabData
                company={company}
                propertyChange={propertyChange}
                isGusLoading={isGusLoading}
                findByNip={findByNip}
                readOnly={props.readOnly}
              />
            </Tab>
            <Tab eventKey="invoicing" title="Fakturowanie" className="mb-2">
              <CompanyEditorTabInvoicing
                company={company}
                propertyChange={propertyChange}
                readOnly={props.readOnly}
              />
            </Tab>
            <Tab eventKey="new" title="Skierowania zewnętrzne" className="mb-2">
              <CompanyEditorTabExternalReferral
                company={company}
                propertyChange={propertyChange}
                batteries={batteries}
                handleClick={handleClick}
                readOnly={props.readOnly}
              />
            </Tab>
          </Tabs>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
          {props.id && (
            <LastEditorInfo
              className="mt-3 color-primary"
              id={props.id}
              method={spd.companies.getLastEditor}
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={props.handleClose}>
            Zamknij
          </Button>
          {!props.readOnly && (
            <Button variant="outline-primary" onClick={createCompany}>
              Zapisz
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
